// Detecta deploys nuevos sin depender exclusivamente del lifecycle del SW.
//
// Why: en moviles el SW viejo puede seguir sirviendo assets cacheados aunque
// se cierren pestañas. Aca usamos dos canales independientes para detectar la
// version desplegada:
//   1) Polling de /version.json (cada N min y al volver visible la pestaña).
//   2) Header X-App-Version inyectado por el interceptor de axios en cada
//      respuesta del backend (heartbeat gratis).
//
// Cada canal tiene su propio "baseline":
//   - Para version.json: el valor leido al arranque (la version del bundle
//     servida cuando se cargo la pagina).
//   - Para X-App-Version: el primer header visto en la sesion (no podemos
//     comparar contra version.json porque son strings con formatos distintos).
// Cuando el valor actual de un canal difiere de su baseline, emitimos
// APP_UPDATE_AVAILABLE_EVENT para que la UI muestre el banner "Actualizar".

import { emitAppUpdateAvailable } from './swUpdateEvents';
import { broadcastSwUpdate, subscribeSwUpdateBroadcast } from './swUpdateBroadcast';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;

let frontendBaseline: string | null = null;
let backendBaseline: string | null = null;
let lastAnnouncedRemote: string | null = null;
let pollHandle: number | null = null;
let crossTabBound = false;
// Cuando una pestaña aplica el update broadcastea 'update-applying' y las
// demas recargan. Marcamos para evitar doble reload si la propia pestaña ya
// inicio applyPendingUpdate.
let applyingLocally = false;

async function fetchFrontendVersion(): Promise<string | null> {
  try {
    const resp = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data && typeof data.version === 'string') return data.version;
    return null;
  } catch {
    return null;
  }
}

function announce(remote: string, source: string) {
  if (remote === lastAnnouncedRemote) return;
  lastAnnouncedRemote = remote;
  const current = source === 'api-header' ? backendBaseline ?? '' : frontendBaseline ?? '';
  emitAppUpdateAvailable({ current, remote, source });
  // Avisar a las otras pestañas. Si esto vino de un broadcast remoto, source
  // es 'cross-tab' y no re-broadcasteamos para no rebotar el mensaje.
  if (source !== 'cross-tab') {
    broadcastSwUpdate({ type: 'update-available' });
  }
}

// Suscripcion al canal compartido entre pestañas. Se invoca una sola vez desde
// initVersionCheck. Idempotente.
function bindCrossTabUpdates() {
  if (crossTabBound) return;
  crossTabBound = true;
  subscribeSwUpdateBroadcast((msg) => {
    if (msg.type === 'update-available') {
      // Otra pestaña detecto un deploy. Mostrar el banner aca tambien.
      // Usamos una marca remota sintetica para que announce() no re-emita el
      // broadcast (evita loops) pero igual dispare el evento local si todavia
      // no se anuncio en esta pestaña.
      const remoteMark = `cross-tab:${Date.now()}`;
      if (lastAnnouncedRemote !== null) return; // ya hay banner mostrado
      announce(remoteMark, 'cross-tab');
      return;
    }
    if (msg.type === 'update-applying') {
      // Otra pestaña esta aplicando el update. Recargamos para no quedar con
      // el bundle viejo en memoria. Si esta pestaña ya inicio applyPendingUpdate
      // localmente, su propio flujo manejara el reload.
      if (applyingLocally) return;
      applyingLocally = true;
      window.location.reload();
    }
  });
}

export function getLoadedVersion(): string | null {
  return frontendBaseline;
}

// Llamado por el interceptor de axios cuando el backend devuelve X-App-Version.
// La primera respuesta de la sesion solo establece el baseline; las siguientes
// que difieran disparan el banner.
export function reportRemoteVersion(remote: string | null | undefined) {
  if (!remote) return;
  if (backendBaseline === null) {
    backendBaseline = remote;
    return;
  }
  if (remote !== backendBaseline) {
    announce(remote, 'api-header');
  }
}

export async function checkForUpdate(source = 'manual') {
  if (!frontendBaseline) return;
  const remote = await fetchFrontendVersion();
  if (!remote) return;
  if (remote !== frontendBaseline) {
    announce(remote, source);
  }
}

export async function initVersionCheck() {
  // Engancharse al canal entre pestañas aunque no haya baseline: una pestaña
  // hermana puede detectar el deploy primero y avisarnos por broadcast.
  bindCrossTabUpdates();

  frontendBaseline = await fetchFrontendVersion();
  if (!frontendBaseline) return; // Sin baseline no podemos comparar; abortar silenciosamente.

  if (pollHandle != null) window.clearInterval(pollHandle);
  pollHandle = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate('poll');
    }
  }, POLL_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate('visibility');
    }
  });

  window.addEventListener('focus', () => {
    void checkForUpdate('focus');
  });
}

// Aplica el update: pide al SW en espera que active y recarga. Si no hay
// SW disponible, hace reload duro como fallback.
export async function applyPendingUpdate() {
  // Marcar antes de broadcastear para que, si alguna pestaña hermana nos
  // re-emite el mensaje, no procesemos un reload duplicado.
  applyingLocally = true;
  // Avisar a las otras pestañas para que tambien recarguen.
  broadcastSwUpdate({ type: 'update-applying' });
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update().catch(() => undefined);
        const waiting = registration.waiting;
        if (waiting) {
          let reloaded = false;
          const onChange = () => {
            if (reloaded) return;
            reloaded = true;
            window.location.reload();
          };
          navigator.serviceWorker.addEventListener('controllerchange', onChange, { once: true });
          waiting.postMessage({ type: 'SKIP_WAITING' });
          window.setTimeout(onChange, 5000); // safety net
          return;
        }
      }
    }
  } catch {
    // fallback de abajo
  }
  window.location.reload();
}
